
import { defineComponent, ref, computed, watch, onMounted, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import VueLottiePlayer from 'vue-lottie-player';
import { useWindowSize } from '@vueuse/core';

import { apiUrl, configureInterceptor } from '@/config/api';
import UserSettingsModal from '@/components/user-settings/Modal.vue';
import TermsAndConditionsModal from '@/components/terms-and-conditions/Modal.vue';

import generateGravatar from '@/utils/generateGravatar';

import defaultCompanyLogo from '@/assets/logo-tracker.svg';
import defaultProfileLogo from '@/assets/navigation/demo-profile.png';
import { useInterval } from '@vueuse/core';

import iconMapSearch from '@/assets/_svg/toolbar/map_search.svg';
import iconMapLayers from '@/assets/_svg/toolbar/map_layers.svg';
import iconMapGeofences from '@/assets/_svg/toolbar/map_geofences.svg';
import iconMapAutoRefresh from '@/assets/_svg/toolbar/map_auto_refresh.svg';
import iconMapManualRefresh from '@/assets/_svg/toolbar/map_refresh.svg';
import iconMapType from '@/assets/_svg/toolbar/map_type.svg';
import cloudMessagingPermission from '@/firebase/init';

interface IUserPropertySettings {
    value: number | boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    icon?: any;
    name: string;
    error: boolean;
    hidden?: boolean;
}

export interface IUserSettings {
    switch: Array<IUserPropertySettings>;
    toolbar: Array<IUserPropertySettings>;
    deviceGroupingNumber: IUserPropertySettings;
    refreshInterval: IUserPropertySettings;
    deviceInactiveTime: IUserPropertySettings;
    deviceWithoutContactTime: IUserPropertySettings;
    allowSubscriptions: IUserPropertySettings;
}

export default defineComponent({
    name: 'Layout',
    components: {
        'user-settings-modal': UserSettingsModal,
        'terms-and-conditions-modal': TermsAndConditionsModal,
        vLottiePlayer: VueLottiePlayer,
    },
    setup() {
        const store = useStore();
        const i18n = useI18n({ useScope: 'global' });
        const serverLocale = computed(() => store.getters['ui/languageCode']);
        i18n.locale.value = serverLocale.value;
        const isUserModalVisible = computed(() => store.getters['ui/isUserModalVisible']);
        const activePendingRequests = ref([]);
        const isPending = ref(false);
        const { width } = useWindowSize();

        const showTraffic = computed(
            () =>
                store.getters['auth/userSettingString'](
                    'usersetting.customer.devicemap.defaultpage.map.showtraffic',
                ) === 'True',
        );

        const deviceGroupping = computed(() => {
            if (
                store.getters['auth/userSettingString'](
                    'usersetting.customer.devicemap.defaultpage.map.devicegroupping',
                ) === 'True'
            ) {
                return true;
            } else if (
                store.getters['auth/userSettingString'](
                    'usersetting.customer.devicemap.defaultpage.map.devicegroupping',
                ) === null &&
                store.getters['device/readNumberOfDevices'] > 500
            ) {
                return true;
            } else {
                return false;
            }
        });

        const deviceGroupingNumber = computed(() => {
            const value = parseInt(
                store.getters['auth/userSettingString'](
                    'usersetting.customer.devicemap.defaultpage.map.devicegroupingnumber',
                ),
            );
            if (value) {
                return value;
            }
            return 3; // default size
        });

        const deviceInactiveTime = computed(() => {
            const value = parseInt(
                store.getters['auth/userSettingString'](
                    'usersetting.customer.devicemap.detailpage.map.deviceinactivetime',
                ),
            );
            if (value) {
                return value;
            }
            return 120; // default size
        });

        const deviceWithoutContactTime = computed(() => {
            const value = parseInt(
                store.getters['auth/userSettingString'](
                    'usersetting.customer.devicemap.detailpage.map.devicewithoutcontacttime',
                ),
            );
            if (value) {
                return value;
            }
            return 180; // default size
        });

        const refreshInterval = computed(() => {
            const value = parseInt(
                store.getters['auth/userSettingString']('usersetting.customer.devicemap.detailpage.map.countdownsec'),
            );
            if (value) {
                return value;
            }
            return 30; // default size
        });

        const defaultSettings = computed(() => {
            const value = store.getters['auth/userSettingString'](
                'usersetting.customer.devicemap.detailpage.map.defaultmap',
            );
            if (value) {
                return value;
            }
            return 'map_id'; // default map type (Map with POI)
        });

        //Toolbar settings
        const hideManualRefreshSettings = computed(() => {
            if (
                store.getters['auth/userSettingString'](
                    'usersetting.customer.devicemap.defaultpage.hideManualRefresh',
                ) === 'False'
            ) {
                return true;
            } else {
                return false;
            }
        });

        const hideAutoRefreshSettings = computed(() => {
            if (
                store.getters['auth/userSettingString'](
                    'usersetting.customer.devicemap.defaultpage.hideAutoRefresh',
                ) === 'False'
            ) {
                return true;
            } else {
                return false;
            }
        });

        const hideSearchSettings = computed(() => {
            if (
                store.getters['auth/userSettingString']('usersetting.customer.devicemap.defaultpage.hideSearch') ===
                'False'
            ) {
                return true;
            } else {
                return false;
            }
        });

        const hideMapStyleSettings = computed(() => {
            if (
                store.getters['auth/userSettingString']('usersetting.customer.devicemap.defaultpage.hideMapStyle') ===
                'False'
            ) {
                return true;
            } else {
                return false;
            }
        });

        const hideSelectLayersSettings = computed(() => {
            if (
                store.getters['auth/userSettingString'](
                    'usersetting.customer.devicemap.defaultpage.hideSelectLayers',
                ) === 'False'
            ) {
                return true;
            } else {
                return false;
            }
        });

        const hideSelectGeofencesSettings = computed(() => {
            if (
                store.getters['auth/userSettingString'](
                    'usersetting.customer.devicemap.defaultpage.hideSelectGeofences',
                ) === 'False'
            ) {
                return true;
            } else {
                return false;
            }
        });

        /* const tokenFCM = computed(() => {
            const value = store.getters['auth/userSettingString']('tokenFCM');
            if (value) {
                try {
                    return JSON.parse(value);
                } catch (e) {
                    console.log('error tokenFCM', e);
                }
            }
            return [];
        }); */

        const isAllowedSubscriptions = computed(() => {
            if (store.getters['auth/userSettingString']('allowSubscriptions') === 'True') {
                return true;
            } else {
                return false;
            }
        });

        const userSettings = computed(() => {
            return {
                switch: [
                    {
                        name: 'map_traffic',
                        value: showTraffic.value,
                        error: false,
                        hidden: false,
                    },
                    {
                        name: 'device_grouping',
                        value: deviceGroupping.value,
                        error: false,
                        hidden: false,
                    },
                ],
                toolbar: [
                    {
                        name: 'hide_map_style_settings',
                        icon: iconMapType,
                        value: !hideMapStyleSettings.value,
                        error: false,
                        hidden: false,
                    },
                    {
                        name: 'hide_manual_refresh_settings',
                        icon: iconMapManualRefresh,
                        value: !hideManualRefreshSettings.value,
                        error: false,
                        hidden: false,
                    },
                    {
                        name: 'hide_auto_refresh_settings',
                        icon: iconMapAutoRefresh,
                        value: !hideAutoRefreshSettings.value,
                        error: false,
                        hidden: false,
                    },
                    {
                        name: 'hide_search_settings',
                        icon: iconMapSearch,
                        value: !hideSearchSettings.value,
                        error: false,
                        hidden: false,
                    },
                    {
                        name: 'hide_select_layers_settings',
                        icon: iconMapLayers,
                        value: !hideSelectLayersSettings.value,
                        error: false,
                        hidden: false,
                    },
                    {
                        name: 'hide_select_geofences_settings',
                        icon: iconMapGeofences,
                        value: !hideSelectGeofencesSettings.value,
                        error: false,
                        hidden: false,
                    },
                ],
                deviceGroupingNumber: {
                    name: 'device_grouping_number',
                    value: deviceGroupingNumber.value,
                    error: false,
                },
                refreshInterval: {
                    name: 'refresh_interval',
                    value: refreshInterval.value,
                    error: false,
                },
                deviceInactiveTime: {
                    name: 'device_inactive',
                    value: deviceInactiveTime.value,
                    error: false,
                },
                deviceWithoutContactTime: {
                    name: 'device_without_contact_time',
                    value: deviceWithoutContactTime.value,
                    error: false,
                },
                allowSubscriptions: {
                    name: 'allow_subscriptions',
                    value: isAllowedSubscriptions.value,
                    error: false,
                    hidden: false,
                },
            };
        });

        watch(serverLocale, () => {
            i18n.locale.value = serverLocale.value;
        });

        const modalLogoutOpen = ref(false);
        const logoutEvent = () => {
            store.dispatch('auth/logout');
        };

        const mobileNavOpen = ref(false);

        const userName = computed(() => {
            return store.state.auth.account?.DisplayName;
        });

        const userEmail = computed(() => {
            return `${store.state.auth.account?.UserName}`;
        });

        const gtcAccepted = computed(() => {
            return store.state.auth.account?.RequireGTCAccept === true ? false : true;
        });

        const isTermsAndConditionsModalOpen = computed(() => store.getters['ui/isTermsAndConditionsModalOpen']);

        const companyUrl = computed(() => {
            let link = store.state.auth.account?.Customer?.BadgeImageUrl;
            if (link && link !== '') {
                if (!(link.indexOf('://') && !link.indexOf('https://'))) {
                    link = 'http://www.' + link.substring(link.indexOf('.') + 1);
                }
            }
            return link;
        });

        const companyPicture = computed(() => {
            const storedImage = store.state.auth.account?.Customer?.BadgeImageName;
            if (storedImage) {
                const url = apiUrl + '/trk/common/image/badge/';
                return url + storedImage;
            }
            return defaultCompanyLogo;
        });

        const userPicture = ref(defaultProfileLogo);

        watchEffect(async () => {
            let email = store.state.auth.account?.Email;
            let name = userEmail.value;

            if (!name) {
                const generatedLogo = await generateGravatar(email, name);
                userPicture.value = generatedLogo ? generatedLogo : defaultProfileLogo;
                return;
            }

            const userNameTmp = name.split(' ');

            if (userNameTmp.length >= 2) {
                name = `${userNameTmp[0]} ${userNameTmp[1]}`;
            }

            const generatedLogo = await generateGravatar(email, name);

            userPicture.value = generatedLogo ? generatedLogo : defaultProfileLogo;
        });

        const closeUserModalVisibility = (): void => {
            store.commit('ui/closeUserModalVisibility');
        };

        const closeTermsAndConditionsModal = () => store.commit('ui/closeTermsAndConditionsModal');

        const { counter: requestsPollingChecker } = useInterval(500, { controls: true });

        const updateFirebaseToken = (/* token: string */): void => {
            return;
            /* const tokensArr = tokenFCM.value;

            if (
                !tokensArr.some((token) => token.token === token && token.environment === process.env.VUE_APP_NAME) &&
                isAllowedSubscriptions.value
            ) {
                store.dispatch('auth/updateFirebaseToken', token);
                return;
            } */
        };

        const permissionGranted = (isGranded): void => {
            if (isGranded) {
                store.commit('ui/closeNotificationMessage');
            } else {
                store.commit('ui/openNotificationMessage');
            }
        };

        const openUserModalSettings = (): void => {
            modalLogoutOpen.value = true;
        };

        watch([activePendingRequests, requestsPollingChecker], () => {
            const startTime = new Date().getTime();
            // checking if exist pending request with startTime longer then:
            const foundPendingRequest = activePendingRequests.value.filter((el) => el.startTime + 2000 < startTime);
            if (foundPendingRequest.length !== 0) {
                isPending.value = true;
            }
        });

        watch([isPending, activePendingRequests], () => {
            if (activePendingRequests.value.length === 0) {
                setTimeout(() => {
                    if (activePendingRequests.value.length === 0) {
                        isPending.value = false;
                    }
                }, 1000);
            }
        });

        watch([gtcAccepted], () => {
            if (gtcAccepted.value) {
                store.commit('ui/openTermsAndConditionsModal');
            }
        });

        onMounted(() => {
            configureInterceptor(
                logoutEvent,
                (url) => {
                    activePendingRequests.value = [
                        ...activePendingRequests.value,
                        {
                            startTime: new Date().getTime(),
                            url,
                        },
                    ];
                },
                (url) => {
                    const activePendingRequestsFiltered = activePendingRequests.value.filter(
                        (request) => request.url !== url,
                    );
                    activePendingRequests.value = [...activePendingRequestsFiltered];
                },
            );
            cloudMessagingPermission(updateFirebaseToken, permissionGranted);
        });

        return {
            width,
            modalLogoutOpen,
            logoutEvent,
            mobileNavOpen,
            userName,
            userEmail,
            companyPicture,
            serverLocale,
            userSettings,
            deviceGroupping,
            showTraffic,
            userPicture,
            companyUrl,
            defaultSettings,
            isUserModalVisible,
            isPending,
            gtcAccepted,
            isTermsAndConditionsModalOpen,
            closeUserModalVisibility,
            closeTermsAndConditionsModal,
            openUserModalSettings,
        };
    },
});
